/**
 * TaskDrawerCampaigns Component
 *
 * Renders the related campaigns tab content:
 * - Campaign cards with clickable links
 * - Campaign status and date range display
 * - Responsive grid layout
 *
 * Accessibility:
 * - Keyboard navigable campaign cards
 * - ARIA labels for interactive elements
 * - Focus indicators for card selection
 *
 * @see WCAG 2.1 AA - 2.1.1 Keyboard
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Tag } from 'antd';
import Text from 'antd/lib/typography/Text';
import '../../styles/index.scss';

/**
 * Format date range for display
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @returns {string} Formatted date range
 */
const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  const start = new Date(startDate).toLocaleDateString();
  const end = new Date(endDate).toLocaleDateString();
  return `${start} - ${end}`;
};

const TaskDrawerCampaigns = ({ campaigns }) => {
  if (!campaigns || campaigns.length === 0) {
    return (
      <div className="task-drawer-full__campaigns-tab">
        <Text type="secondary">No related campaigns</Text>
      </div>
    );
  }

  const handleCampaignClick = (campaignId) => {
    window.open(`/campaigns/details/${campaignId}`, '_blank', 'noopener,noreferrer');
  };

  const handleCampaignKeyDown = (event, campaignId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCampaignClick(campaignId);
    }
  };

  return (
    <div className="task-drawer-full__campaigns-tab">
      <div
        className="task-drawer-full__campaigns-grid"
        role="list"
        aria-label="Related campaigns"
      >
        {campaigns.map((campaign) => (
          <div
            key={campaign.id}
            className="task-drawer-full__campaign-card"
            onClick={() => handleCampaignClick(campaign.id)}
            onKeyDown={(e) => handleCampaignKeyDown(e, campaign.id)}
            role="listitem"
            tabIndex={0}
            aria-label={`Campaign: ${campaign.name}${campaign.status ? `, Status: ${campaign.status}` : ''}`}
          >
            <div className="task-drawer-full__campaign-header">
              <Text strong className="task-drawer-full__campaign-name">
                {campaign.name}
              </Text>
              {campaign.status && (
                <Tag color="blue" className="task-drawer-full__campaign-status">
                  {campaign.status}
                </Tag>
              )}
            </div>
            {campaign.startDate && campaign.endDate && (
              <div className="task-drawer-full__campaign-dates">
                <span aria-hidden="true">📅</span>
                <span>
                  {formatDateRange(campaign.startDate, campaign.endDate)}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

TaskDrawerCampaigns.propTypes = {
  campaigns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    })
  ),
};

TaskDrawerCampaigns.defaultProps = {
  campaigns: [],
};

export default TaskDrawerCampaigns;
